import { formatText } from 'bernie-l10n';
import * as React from 'react';
import { observer } from 'mobx-react-lite';
import { UitkTextInput } from 'uitk-react-input';
import { getUpdatedFilterQueryObject } from '../../components/srp/sort-filter/filter-helper';
import { useStores } from '../../utils/hooks';
import { refreshAds } from '../../utils/refresh-ads';
import { getPageId } from '../../helpers/common-helper';
import { ActivityDestinationViewSearch, ActivityGroupViewType } from '../../common/__generated__/api';

const NOT_FOUND_INDEX = -1;
const ENTER_CHAR_CODE = 13;

export interface KeywordTextInputProps {
  isCarousel?: boolean;
  inDialog?: boolean;
}

export const KeywordTextInput: React.FC<KeywordTextInputProps> = observer(({ isCarousel, inDialog }) => {
  const { analytics, activitySearchStore, routeStore } = useStores();
  const tileIndex = (activitySearchStore.activityGroups as ActivityDestinationViewSearch.ActivityGroups[]).findIndex(
    activityGroup => activityGroup.viewType === ActivityGroupViewType.KEYWORD_SEARCH
  );
  let keywordData = {
    analytics: {
      linkName: 'keyword-search-done',
      referrerId: `${getPageId(activitySearchStore)}.filterSort.SearchActivity`
    },
    placeholder: activitySearchStore.sortAndFilterObject.keywordPlaceholder,
    keyword: activitySearchStore.sortAndFilterObject.keywordValue
  };

  if (isCarousel && tileIndex > NOT_FOUND_INDEX) {
    keywordData = {
      analytics: activitySearchStore.activityGroups[tileIndex].activityTiles[0].analytics,
      placeholder: activitySearchStore.activityGroups[tileIndex].activityTiles[0].placeholder || '',
      keyword: activitySearchStore.activityGroups[tileIndex].activityTiles[0].searchTerm || ''
    };
  }

  const handleClear = (): void => {
    if (isCarousel || !inDialog) {
      const newQueryString = routeStore.mergeQueryString(routeStore.search, {
        ...getUpdatedFilterQueryObject(activitySearchStore),
        sort: activitySearchStore.sortAndFilterObject.initialSelectedSort,
        slimSearchKeyword: '',
        seeAll: activitySearchStore.sortAndFilterObject.initialSeeAllOption,
        pwaDialog: ''
      });
      routeStore.updateSearch(newQueryString, {}, true);
    } else {
      activitySearchStore.sortAndFilterObject.keywordValue = '';
    }
  };

  const updateKeyword = (event: React.ChangeEvent<HTMLInputElement>): void => {
    if (isCarousel) {
      activitySearchStore.activityGroups[tileIndex].activityTiles[0].searchTerm = event.target.value;
    } else {
      activitySearchStore.sortAndFilterObject.keywordValue = event.target.value;
    }
  };

  const handleKeywordSubmit = (event: React.KeyboardEvent<HTMLInputElement>): void => {
    if (event.charCode === ENTER_CHAR_CODE || event.which === ENTER_CHAR_CODE) {
      const newQueryString = routeStore.mergeQueryString(routeStore.search, {
        ...getUpdatedFilterQueryObject(activitySearchStore),
        sort: activitySearchStore.sortAndFilterObject.initialSelectedSort,
        slimSearchKeyword: isCarousel
          ? activitySearchStore.activityGroups[tileIndex].activityTiles[0].searchTerm
          : activitySearchStore.sortAndFilterObject.keywordValue,
        seeAll: activitySearchStore.sortAndFilterObject.initialSeeAllOption,
        pwaDialog: ''
      });

      const keywordEnteredInPageInputField =
        isCarousel && activitySearchStore.activityGroups[tileIndex].activityTiles[0].searchTerm?.length > 0;
      if (activitySearchStore.sortAndFilterObject.keywordValue.length > 0 || keywordEnteredInPageInputField) {
        analytics.trackEvent(
          keywordData.analytics && keywordData.analytics.referrerId,
          keywordData.analytics && keywordData.analytics.linkName,
          analytics.omnitureData,
          null,
          true
        );
      }

      routeStore.updateSearch(newQueryString, {}, true);
      refreshAds();
    }
  };

  return (
    <UitkTextInput
      data-testid="keyword-text-input--input"
      icon="search"
      label={formatText('lx.search.freetext.label')}
      name="searchKeyword"
      id="searchKeyword"
      placeholder={keywordData.placeholder || formatText('lx.search.freetext.placeholder')}
      hasVisibleLabel={false}
      onChange={updateKeyword}
      onKeyPress={handleKeywordSubmit}
      value={keywordData.keyword}
      hasClearButton={true}
      handleClear={handleClear}
    />
  );
});
